import type React from 'react';
import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { Form } from 'react-bootstrap';
import { useSWRConfig } from 'swr';
import {
  Breadcrumbs,
  Button,
  designTokens,
  Flex,
  Panel,
  PanelBody,
  PanelHeader
} from '@itandi/itandi-bb-ui';
import Link from 'next/link';
import { VendorSettingLayout } from '~/components/common/layouts/settings/VendorSettingLayout';
import { VendorLayoutV2 } from '~/components/common/layouts/VendorLayoutV2';
import { PATH as COMPANY_SETTINGS_PATH } from '~/pages/vendors/settings/company_settings';
import Styles from './ConstructionItemSettingList.module.scss';
import SettingStyles from '../../SettingStyle.module.scss';
import { PATH as SETTING_PATH } from '~/pages/vendors/settings';
import { useConstructionItems } from '~/hooks/vendors/useConstructionItems';
import {
  buildIndexPath,
  useConstructionItemSettings
} from '~/hooks/vendors/useConstructionItemSettings';
import { useCompanySettings } from '~/hooks/vendors/useCompanySettings';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import type { ConstructionItemSettingEdit } from '~/models/ConstructionItemSetting';
import { validationSchemaConstructionItemSettingForm } from '~/models/ConstructionItemSetting';
import { create as createSetting } from '~/utils/api/vendors/ConstructionItemSetting';
import { isSuccess } from '~/utils/api/api_handler';
import { toThousandSeparator } from '~/utils/CurrencySeparator';

type Props = {
  companyMasterId: number;
};

export const Page: React.FC<Props> = ({ companyMasterId }) => {
  const [editing, setEditing] = useState(false);
  const {
    isOpen: confirmOpen,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose
  } = useOpenHandler();
  const { mutate: globalMutate } = useSWRConfig();
  const router = useRouter();
  const { data: constructionItems } = useConstructionItems().index();
  const { data: constructionItemSettings } =
    useConstructionItemSettings(companyMasterId);
  const { data: companySetting } = useCompanySettings().show(companyMasterId);

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isDirty }
  } = useForm<{ settings: ConstructionItemSettingEdit[] }>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchemaConstructionItemSettingForm),
    defaultValues: {
      settings: constructionItemSettings ?? []
    }
  });
  const values = watch('settings');

  const handleChangeItemSetting = useCallback(
    (itemId: number, inputValue: string): void => {
      const currentValues = [...values];
      const index = currentValues.findIndex(
        (setting) => setting.constructionItemId === itemId
      );
      const parsedValue = Number(inputValue);
      const valueNum =
        inputValue === '' || Number.isNaN(parsedValue) ? null : parsedValue;
      if (index === -1) {
        currentValues.push({
          unitAmount: valueNum,
          constructionItemId: itemId
        });
      } else {
        const itemSetting = currentValues[index];
        const setting = {
          ...itemSetting,
          unitAmount: valueNum,
          constructionItemId: itemId
        };
        currentValues.splice(index, 1, setting);
      }
      setValue('settings', currentValues, {
        shouldDirty: true,
        shouldTouch: true
      });
      trigger('settings');
    },
    [values, setValue, trigger]
  );

  const onSubmit = handleSubmit(async (formData): Promise<void> => {
    const result = await createSetting(formData.settings, companyMasterId);
    if (isSuccess(result)) {
      await globalMutate(buildIndexPath);
      await router.push(COMPANY_SETTINGS_PATH);
      toast.success('保存しました。');
    } else {
      toast.error(`エラーが発生しました。${result.message}`);
    }
  });

  if (
    constructionItemSettings === null ||
    constructionItems === null ||
    companySetting === null
  ) {
    return null;
  }

  return (
    <VendorLayoutV2>
      <VendorSettingLayout>
        <div className={SettingStyles.PcBreadcrumbsWrapper}>
          <Breadcrumbs
            items={[
              {
                label: '提携中の管理会社一覧',
                as: Link,
                href: COMPANY_SETTINGS_PATH
              },
              { label: '工事単価一覧' }
            ]}
          />
        </div>
        <div className={SettingStyles.SpBreadcrumbsWrapper}>
          <Breadcrumbs
            items={[
              { label: '設定一覧', as: Link, href: SETTING_PATH },
              {
                label: '提携中の管理会社一覧',
                as: Link,
                href: COMPANY_SETTINGS_PATH
              },
              { label: '工事単価一覧' }
            ]}
          />
        </div>
        <Panel>
          <PanelHeader
            rightArea={
              editing ? (
                <Flex gap={designTokens['spacing/xxs']}>
                  <Button
                    onClick={(): void => {
                      setEditing(false);
                      reset();
                    }}
                    variant="secondary"
                  >
                    キャンセル
                  </Button>
                  <Button onClick={handleConfirmOpen} variant="primary">
                    更新
                  </Button>
                </Flex>
              ) : (
                <Button
                  onClick={(): void => setEditing(true)}
                  variant="primary"
                >
                  編集
                </Button>
              )
            }
            title={`${companySetting.name} 工事単価一覧`}
          />
          <PanelBody>
            <div>
              <table className={Styles.List}>
                <thead>
                  <tr className={clsx(Styles.ListRow, Styles.Header)}>
                    <th>項目名</th>
                    <th>単価</th>
                  </tr>
                </thead>
                <tbody>
                  {constructionItems.map((item) => {
                    const index = values.findIndex(
                      (setting) => setting.constructionItemId === item.id
                    );
                    const itemSetting =
                      index === -1 ? undefined : values[index];
                    const unitAmount = itemSetting
                      ? itemSetting.unitAmount
                      : item.unitAmount;
                    const fieldError =
                      index >= 0 ? errors.settings?.[index] : undefined;
                    return (
                      <tr key={item.id} className={clsx(Styles.ListRow)}>
                        <td className={clsx(Styles.ListCol, Styles.PcCol)}>
                          <div className={Styles.ConstructionItemName}>
                            {item.name}
                          </div>
                          <p className={Styles.DefaultAmount}>
                            初期値: ¥{toThousandSeparator(item.unitAmount)}
                          </p>
                        </td>
                        <td className={clsx(Styles.ListCol, Styles.Edit)}>
                          {editing && item.overridable ? (
                            <>
                              <Form.Control
                                isInvalid={!!fieldError}
                                onChange={(e): void => {
                                  handleChangeItemSetting(
                                    item.id,
                                    e.target.value
                                  );
                                }}
                                placeholder="単価"
                                type="number"
                                value={unitAmount ?? ''}
                              />
                              <Form.Control.Feedback type="invalid">
                                {fieldError?.unitAmount?.message}
                              </Form.Control.Feedback>
                            </>
                          ) : (
                            <>
                              {unitAmount &&
                                `¥${toThousandSeparator(unitAmount)}`}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </PanelBody>
        </Panel>
        <ConfirmModal
          actionText="更新"
          confirmType="save"
          disabled={!isDirty}
          handleAccept={onSubmit}
          handleCancel={handleConfirmClose}
          isOpen={confirmOpen}
        >
          単価を更新してよろしいですか？
        </ConfirmModal>
      </VendorSettingLayout>
    </VendorLayoutV2>
  );
};
